/**
 * Public /pricing page: header + plan cards (prices from plan config, no promo
 * or discount) + pricing-tagged FAQ + readiness CTA + footer. Text comes from
 * the `pricing_page` site setting.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { usePublicSettings } from "@/lib/siteSettings";
import { renderRich } from "@/lib/richText";
import PricingCards from "@/components/PricingCards";
import { ChevronDown, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = { headline: "Simple pricing", sub_line: "Start free. Prices in AUD.", refund_line: "Try Pro free for 7 days, cancel anytime", button_label: "Take the free readiness check" };

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const settings = usePublicSettings();
  const t = { ...DEFAULTS, ...(settings["pricing_page"] || {}) };
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    document.title = "Pricing - PassADC | Free & Pro plans for ADC Part 1";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    const prev = m.content;
    m.content = "PassADC pricing: start free, or go Pro from AUD $19/month, or get the March 2027 Pass for $89 one-time. 1,200+ exam-style MCQs, readiness trials and a 24/7 tutor for the ADC Part 1 written exam.";
    axios.get(`${API}/public/faq?page=pricing`).then((r) => setFaqs(r.data?.items || [])).catch(() => {});
    return () => { if (prev) m.content = prev; };
  }, []);

  const goPro = (card) => {
    const plan = card?.plan_key;
    if (!plan) return navigate("/signup");           // Free card → sign up
    if (user) return navigate(`/billing?plan=${plan}`);   // start checkout for this plan
    navigate(`/signup?checkout=${plan}`);            // sign up, then continue to checkout
  };

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <header className="px-6 pt-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2"><BrandLogo size={24} /><span className="font-bold text-base">PassADC</span></button>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => navigate(user ? "/dashboard" : "/login")} data-testid="pricing-signin">{user ? "Dashboard" : "Sign in"}</Button>
        </div>
      </header>

      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-center mb-3" data-testid="pricing-headline">{t.headline}</h1>
          <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto text-sm">{t.sub_line}</p>

          <PricingCards onSelect={goPro} />

          <p className="text-center text-xs text-muted-foreground mt-8" data-testid="pricing-refund-line">
            <a href="/refund" className="underline hover:text-foreground">{t.refund_line}</a>
          </p>

          {faqs.length > 0 && (
            <div className="max-w-3xl mx-auto mt-16 space-y-3" data-testid="pricing-faq">
              {faqs.map((f, i) => (
                <details key={f.id || i} className="group rounded-2xl border border-border bg-card px-6 py-4" data-testid={`pricing-faq-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-sm md:text-base [&::-webkit-details-marker]:hidden">{f.question}<ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180 flex-shrink-0 ml-3" /></summary>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{renderRich(f.answer, "text-foreground")}</p>
                </details>
              ))}
            </div>
          )}

          <div className="flex justify-center mt-12">
            <Button size="lg" className="rounded-full px-8 py-6 text-base font-semibold" data-testid="pricing-readiness-btn" onClick={() => navigate("/readiness-check")}>
              {t.button_label}<ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PricingPage;
